<script setup>
import { routes } from '@/constants/routes';
import { navigation } from '@/constants/navigation';

const props = defineProps({
  isFixed: {
    type: Boolean,
  },
});

const globalStore = useGlobalStore();

function openMobileMenu() {
  if (!globalStore.isMobileMenuShow) {
    globalStore.openMobileMenu();
  }
}
</script>

<template>
  <header
    class="layout-header"
    :class="{
      [`layout-header--fixed`]: props.isFixed,
    }"
  >
    <div class="layout-header__container container">
      <div class="layout-header__body">
        <div class="layout-header__left">
          <CSpriteIcon
            v-for="item in 4"
            :key="item"
            class="layout-header__icon layout-header__icon--plus"
            :class="`layout-header__icon--plus--${item}`"
            name="plus"
          />

          <NuxtLink class="layout-header__logo" :to="{ path: routes.main }">
            <img
              src="/icons/centourio_logo.svg"
              alt="centourio"
              class="layout-header__icon layout-header__icon--logo"
            />
          </NuxtLink>
        </div>

        <div class="layout-header__right">
          <CSpriteIcon
            v-for="item in 4"
            :key="item"
            class="layout-header__icon layout-header__icon--plus"
            :class="`layout-header__icon--plus--${item + 4}`"
            name="plus"
          />

          <nav
            class="layout-header__navigation layout-header__navigation--desktop"
          >
            <ul class="layout-header__navigation-list">
              <li
                v-for="(navigationItem, index) in navigation"
                :key="index"
                class="layout-header__navigation-item"
              >
                <NuxtLink
                  v-if="navigationItem.id === 'contact-us'"
                  class="layout-header__button layout-header__button--contact-us"
                  :to="{ hash: `#${navigationItem.id}` }"
                >
                  <CSpriteIcon
                    name="icon_mail_14"
                    class="layout-header__icon layout-header__icon--mail"
                  />
                </NuxtLink>

                <NuxtLink
                  v-else
                  class="layout-header__navigation-link"
                  :to="{ hash: `#${navigationItem.id}` }"
                >
                  <span
                    class="layout-header__font layout-header__font--navigation"
                  >
                    {{ navigationItem.label }}
                  </span>
                </NuxtLink>
              </li>
            </ul>
          </nav>

          <nav
            class="layout-header__navigation layout-header__navigation--mobile"
          >
            <button
              type="button"
              class="layout-header__button layout-header__button--burger"
              :class="{
                [`layout-header__button--burger--active`]:
                  globalStore.isMobileMenuShow,
              }"
              @click="openMobileMenu"
            >
              <CSpriteIcon
                name="icon_menu"
                class="layout-header__icon layout-header__icon--burger"
              />
            </button>

            <NuxtLink
              class="layout-header__button layout-header__button--contact-us"
              :to="{ hash: `#contact-us` }"
            >
              <CSpriteIcon
                name="icon_mail_14"
                class="layout-header__icon layout-header__icon--mail"
              />
            </NuxtLink>
          </nav>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.layout-header {
  $parent: &;

  &__font {
    &--navigation {
      font-size: em(14);
      font-weight: 700;
      line-height: 100%;
    }
  }

  &__icon {
    &--logo {
      width: em(210);
      height: em(45);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(160);
        height: auto;
      }
    }

    &--mail {
      width: em(16);
      height: em(16);

      @include media-breakpoint-down(md) {
        width: em(13);
        height: em(13);
      }
    }

    &--burger {
      width: em(13);
      height: em(13);
    }

    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      pointer-events: none;
      opacity: 0.5;

      &--1 {
        top: em(-30);
        left: em(-20);

        @include media-breakpoint-down(md) {
          top: em(-26);
          left: em(-20);
        }

        @include media-breakpoint-down(sm) {
          left: em(-22);
        }
      }

      &--2 {
        top: em(50);
        left: em(-20);

        @include media-breakpoint-down(md) {
          top: em(46);
          left: em(-20);
        }

        @include media-breakpoint-down(sm) {
          top: em(26);
          left: em(-22);
        }
      }

      &--3 {
        top: em(-30);
        right: em(-20);

        @include media-breakpoint-down(md) {
          top: em(-26);
          right: em(-20);
        }
      }

      &--4 {
        top: em(50);
        right: em(-20);

        @include media-breakpoint-down(md) {
          top: em(46);
          right: em(-20);
        }

        @include media-breakpoint-down(sm) {
          top: em(25);
          right: em(-20);
        }
      }

      &--5 {
        top: em(-30);
        left: em(-24);

        @include media-breakpoint-down(md) {
          top: em(-26);
          left: em(-24);
        }
      }

      &--6 {
        top: em(50);
        left: em(-24);

        @include media-breakpoint-down(md) {
          top: em(46);
          left: em(-24);
        }

        @include media-breakpoint-down(sm) {
          top: em(25);
        }
      }

      &--7 {
        top: em(-30);
        right: em(-20);

        @include media-breakpoint-down(md) {
          top: em(-26);
          right: em(-20);
        }
      }

      &--8 {
        top: em(50);
        right: em(-20);

        @include media-breakpoint-down(md) {
          top: em(46);
          right: em(-20);
        }

        @include media-breakpoint-down(sm) {
          top: em(25);
        }
      }
    }
  }

  &__container {
    @include media-breakpoint-down(md) {
      max-width: em(768);
    }
  }

  &__body {
    position: relative;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    padding: em(30) em(20);

    @include media-breakpoint-down(md) {
      gap: em(35);
      padding: em(22) em(12) em(22) em(17);
    }

    @include media-breakpoint-down(sm) {
      padding: em(18) em(15) em(10);
    }
  }

  &__left {
    position: relative;
    display: flex;
    align-items: center;
    width: em(520);
    padding: em(10) em(15);
    border-radius: em(16);
    transition: ease-out $time-fast;
    transition-property: background-color, backdrop-filter;
    will-change: backdrop-filter;

    @include media-breakpoint-down(md) {
      flex-basis: 50%;
      width: auto;
      padding: em(10) em(15) em(7);
    }

    @include media-breakpoint-down(sm) {
      padding: em(5) em(9) em(3);
    }
  }

  &__logo {
  }

  &__right {
    position: relative;
    display: flex;
    align-items: center;
    padding: em(14) em(20);
    border-radius: em(16);
    transition: ease-out $time-fast;
    transition-property: background-color, backdrop-filter;
    will-change: backdrop-filter;

    @include media-breakpoint-down(md) {
      flex-basis: 50%;
      justify-content: flex-end;
    }

    @include media-breakpoint-down(sm) {
      padding: em(5) em(9);
    }
  }

  &__navigation {
    @include media-breakpoint-down(md) {
      display: flex;
    }

    &--desktop {
      @include media-breakpoint-down(md) {
        display: none;
      }
    }

    &--mobile {
      @include media-breakpoint-up(md) {
        display: none;
      }
    }
  }

  &__navigation-list {
    display: flex;
    align-items: center;
  }

  &__navigation-item {
    text-transform: uppercase;

    &:not(:last-child) {
      margin-right: em(19);
    }
  }

  &__button {
    &--contact-us {
      display: flex;
      align-items: center;
      justify-content: center;
      width: em(32);
      height: em(32);
      background-color: $bg-light;
      border-radius: 50%;
      transition: background-color ease-out $time-fast;

      @include hover-active-focus {
        background-color: $color-white;
      }
    }

    &--burger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: em(32);
      height: em(32);
      margin-right: em(8);
      background-color: $bg-light;
      border-radius: 50%;
      transition: background-color ease-out $time-fast;

      @include hover-active-focus {
        background-color: $color-white;
      }

      &--active {
        background-color: $color-white;
      }
    }
  }

  &__navigation-link {
    color: $text-dark-main;
    transition: color ease-out $time-fast;

    @include hover-active-focus {
      color: $accent-red;
    }
  }

  &--fixed {
    #{$parent} {
      &__left {
        background-color: rgba($color-white, 0.1);
        backdrop-filter: blur(5px);
      }

      &__right {
        background-color: rgba($color-white, 0.1);
        backdrop-filter: blur(5px);
      }
    }
  }
}
</style>
